import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaTools, FaShoppingCart, FaShieldAlt, FaCheck } from "react-icons/fa";
import { Button } from "@/components/ui/button";

const Home = () => {
  const services = [
    {
      icon: <FaTools className="text-4xl text-blue-500" />,
      title: "End-to-End IT Services",
      description: "From consulting to implementation, we handle your entire IT needs"
    },
    {
      icon: <FaShoppingCart className="text-4xl text-blue-500" />,
      title: "Smart Procurement",
      description: "We source the right hardware and software at the best prices"
    },
    {
      icon: <FaShieldAlt className="text-4xl text-blue-500" />,
      title: "Secure & Scalable",
      description: "Your business data and network, protected and future-ready"
    }
  ];

  const benefits = [
    "6 specialized services under one roof",
    "Dedicated support for SMBs — no big-company runaround",
    "Transparent pricing, no hidden costs",
    "Based in Bangalore, serving clients pan-India",
    "Certified experts in Cloud, DevOps & Cybersecurity"
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80" 
            alt="Server infrastructure"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Your Trusted IT Mitra for Growing Businesses
              </h1>
              <p className="text-lg sm:text-xl mb-8 text-slate-300">
                DevOpsMitra helps small and medium businesses in Bangalore and across India build, secure, and scale their IT infrastructure — so you can focus on growing your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="get-consultation-btn"
                    className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-8 py-6 text-lg font-semibold"
                  >
                    Get a Free Consultation
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    data-testid="explore-services-btn"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-slate-900 transition-all duration-300 rounded-lg px-8 py-6 text-lg font-semibold"
                  >
                    Explore Our Services
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=800" 
                alt="IT Infrastructure"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">What We Do</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md border border-slate-100 transition-all duration-300 group"
                data-testid={`service-card-${index}`}
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Why Businesses Choose DevOpsMitra
              </h2>
              <p className="text-xl text-slate-600 mb-8 italic">
                "We don't just sell IT — we become your IT team."
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                    data-testid={`benefit-item-${index}`}
                  >
                    <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-700">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800" 
                alt="Team collaboration"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Upgrade Your IT?
            </h2>
            <p className="text-lg sm:text-xl mb-8 text-slate-300 max-w-2xl mx-auto">
              Whether you're setting up from scratch or optimizing what you have, DevOpsMitra is here to help. Let's talk.
            </p>
            <Link to="/contact">
              <Button 
                data-testid="book-call-btn"
                className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-8 py-6 text-lg font-semibold"
              >
                Book a Free Call Today
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
